'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Pause, Play } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import {
  recordingPath,
  recordingPlayable,
  type CallCard,
  type CallCardsBlock,
} from '@/lib/chat/blocks/call-cards';
import { useRenderNow } from '@/lib/time/render-clock';

/**
 * Who rang: one card per call the receptionist took, inside Mucka's bubble.
 *
 * Every word on a card was built on the server from the call row (the tag from
 * its status, the time in London, the summary as stored). Nothing here works
 * anything out except whether a recording has expired since the reply was
 * written, because a stored reply is redrawn long after it was built.
 *
 * Taps only open or play. Opening a page and listening change nothing and
 * reach nobody, so they stay on for an older reply too, as the owed table's
 * rows and the week's stops do; there is no tap here that answers Mucka.
 *
 * 🚨 The audio plays from the gated route, never a stored link: the route
 * checks `manage_voice_receptionist` and redirects to a five-minute signed URL
 * each time, so a recording that has since been deleted fails here, inline,
 * rather than playing from a link that outlived it.
 */
export function CallCards({ block, onOpen }: {
  block: CallCardsBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  // The render clock, not a fresh read: a stored reply hydrates against the
  // moment the page was rendered, so the expiry check cannot flicker.
  const now = useRenderNow();

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  function toggle(card: CallCard) {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing === card.callId) {
      audio.pause();
      setPlaying(null);
      return;
    }
    if (loaded !== card.callId) {
      audio.pause();
      audio.src = recordingPath(card.callId);
      setLoaded(card.callId);
      setProgress(0);
    }
    setFailed(prev => ({ ...prev, [card.callId]: false }));
    setPlaying(card.callId);
    const started = audio.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => {
        setPlaying(current => (current === card.callId ? null : current));
        setFailed(prev => ({ ...prev, [card.callId]: true }));
      });
    }
  }

  const label = `${block.total} call${block.total === 1 ? '' : 's'} ${block.window}`;

  return (
    <div className="mchat-calls">
      <audio
        ref={audioRef}
        preload="none"
        className="mchat-calls-audio"
        onTimeUpdate={e => {
          const a = e.currentTarget;
          setProgress(a.duration > 0 && Number.isFinite(a.duration) ? a.currentTime / a.duration : 0);
        }}
        onEnded={() => { setPlaying(null); setProgress(0); }}
        onError={() => {
          if (loaded) setFailed(prev => ({ ...prev, [loaded]: true }));
          setPlaying(null);
        }}
      />
      <div className="mchat-calls-list" role="list" aria-label={label}>
        {block.cards.map(card => {
          const canPlay = recordingPlayable(card.recording, now);
          const isPlaying = playing === card.callId;
          return (
            <div className="mchat-calls-card" role="listitem" key={card.callId}>
              <button
                type="button"
                className="mchat-calls-head"
                onClick={() => onOpen(card.path)}
                aria-label={`Open the call from ${card.who}, ${card.time}`}
              >
                <span className="mchat-calls-who">{card.who}</span>
                <span className={`mchat-calls-tag mchat-calls-tag--${card.tone}`}>{card.tag}</span>
                <span className="mchat-calls-time">{card.time}</span>
              </button>
              <p className="mchat-calls-summary">{card.summary}</p>
              {canPlay && (
                <div className="mchat-calls-player">
                  <button
                    type="button"
                    className="mchat-calls-play"
                    aria-label={isPlaying ? `Pause the recording of the call from ${card.who}` : `Play the recording of the call from ${card.who}`}
                    aria-pressed={isPlaying}
                    onClick={() => toggle(card)}
                  >
                    <Icon icon={isPlaying ? Pause : Play} size={15} />
                  </button>
                  <span className="mchat-calls-track" aria-hidden="true">
                    <span
                      className="mchat-calls-track-fill"
                      style={{ width: `${loaded === card.callId ? Math.round(progress * 100) : 0}%` }}
                    />
                  </span>
                  {card.recording?.length && <span className="mchat-calls-length">{card.recording.length}</span>}
                </div>
              )}
              {failed[card.callId] && (
                <p className="mchat-calls-error" role="alert">
                  That recording would not play. It may have been deleted; the summary above still stands.
                </p>
              )}
              {card.next && (
                <button type="button" className="mchat-calls-next" onClick={() => onOpen(card.next!.path)}>
                  <span className="mchat-calls-next-label">{card.next.label}</span>
                  <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="mchat-row mchat-calls-all" onClick={() => onOpen(block.allPath)}>
        <span className="mchat-row-main">
          <span className="mchat-row-title">Open all calls</span>
        </span>
        {block.more > 0 && <span className="mchat-row-meta">{block.more} more</span>}
        <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
      </button>
    </div>
  );
}
